import { useState, useMemo } from 'react';
import type { AnalysisType } from '../../shared/types';
import { ANALYSIS_OPTIONS } from '../../shared/types';
import type { SidebarItem } from '../hooks/useAnalysisJobs';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import AnalysisIcon from './ui/AnalysisIcons';
import Avatar from './ui/Avatar';

interface SidebarProps {
  history: SidebarItem[];
  onViewHistory: (item: SidebarItem) => void;
  onRenameHistory: (id: string, newTitle: string) => void;
  onDeleteHistory: (id: string) => void;
  activeItemId?: string | null;
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  onAccountClick: () => void;
  onHomeClick?: () => void;
}

const formatDate = (iso: string) => {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return 'Hier';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

/** Map score to the correct 4-zone color (handles legacy RISQUE_MODERE verdicts) */
const verdictColorFromScore = (score: number) => {
  if (score >= 80) return 'text-emerald-500';
  if (score >= 60) return 'text-yellow-500';
  if (score >= 40) return 'text-orange-500';
  return 'text-red-500';
};

export default function Sidebar({
  history,
  onViewHistory,
  onRenameHistory,
  onDeleteHistory,
  activeItemId,
  isMobileOpen,
  setIsMobileOpen,
  isCollapsed,
  setIsCollapsed,
  onAccountClick,
  onHomeClick,
}: SidebarProps) {
  const { features, user, organization } = useAuth();
  // Mode pastel : la sidebar devient le panneau ENCRE de la charte. Elle
  // porte alors localement la classe `dark` pour réutiliser tels quels ses
  // utilitaires `dark:` (texte clair, logo blanc) — le reste de l'écran
  // reste clair. Aucun effet dans les trois autres thèmes.
  const { theme } = useTheme();
  const pastel = theme === 'pastel';
  const hasHistoryAccess = features.history || (organization?.creditBalance ?? 0) > 0;
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<AnalysisType | 'all'>('all');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Categories that have items in history
  const usedCategories = useMemo(() => {
    const keys = new Set(history.map(h => h.analysisType));
    return ANALYSIS_OPTIONS.filter(opt => keys.has(opt.key));
  }, [history]);

  // Filtered + searched
  const filteredHistory = useMemo(() => {
    return history.filter(item => {
      const matchesFilter = activeFilter === 'all' || item.analysisType === activeFilter;
      const matchesSearch = !search || item.title.toLowerCase().includes(search.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [history, activeFilter, search]);

  // Group by date
  const grouped = useMemo(() => {
    const groups: Record<string, SidebarItem[]> = {};
    filteredHistory.forEach(item => {
      const label = formatDate(item.timestamp);
      if (!groups[label]) groups[label] = [];
      groups[label].push(item);
    });
    return groups;
  }, [filteredHistory]);

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-30 md:hidden transition-opacity ${
          isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileOpen(false)}
      />

      <aside
        className={`${pastel ? 'dark p-panel-ink' : ''} fixed md:relative flex flex-col bg-slate-50 dark:bg-slate-950 shader:bg-black/30 shader:backdrop-blur-2xl border-r border-slate-200 dark:border-slate-800 shader:border-white/10 h-full transition-all duration-300 z-40
          ${isMobileOpen ? (pastel ? 'inset-y-3 left-3 w-72 translate-x-0' : 'translate-x-0 w-72') : (pastel ? '-translate-x-[110%]' : '-translate-x-full')}
          md:translate-x-0 ${isCollapsed ? 'md:w-20' : 'md:w-72'}`}
      >
        <div className="flex flex-col h-full pastel:overflow-hidden">
          {/* ── Header ── */}
          <div className={`border-b border-slate-200 dark:border-slate-800 shader:border-white/10 pastel:border-b-0 h-14 pastel:h-[68px] flex-shrink-0 px-4 pastel:px-5 flex items-center ${isCollapsed ? 'justify-center gap-2' : 'justify-between'}`}>
            {!isCollapsed && (
              <button
                onClick={onHomeClick}
                title="Accueil"
                className="font-display tracking-tight text-[#2e5289] dark:text-white flex items-center gap-2 min-w-0 rounded-md hover:opacity-80 transition-opacity cursor-pointer"
              >
                <img
                  src="/evinly_logo_blue.svg"
                  alt="Evinly"
                  className="w-6 h-6 flex-shrink-0 select-none block dark:hidden shader:hidden"
                  draggable={false}
                />
                <img
                  src="/evinly_logo.svg"
                  alt="Evinly"
                  className="w-6 h-6 flex-shrink-0 select-none hidden dark:block shader:block"
                  draggable={false}
                />
                <span className="truncate text-[1.4rem] pastel:text-[1.55rem] leading-none">Evinly</span>
              </button>
            )}
            {isCollapsed && (
              <button
                onClick={onHomeClick}
                title="Accueil"
                className="rounded-md hover:opacity-80 transition-opacity cursor-pointer"
              >
                <img
                  src="/evinly_logo_blue.svg"
                  alt="Evinly"
                  className="w-7 h-7 select-none block dark:hidden shader:hidden"
                  draggable={false}
                />
                <img
                  src="/evinly_logo.svg"
                  alt="Evinly"
                  className="w-7 h-7 select-none hidden dark:block shader:block"
                  draggable={false}
                />
              </button>
            )}
            {/* Collapse toggle — desktop only */}
            {/* Mode pastel : pastille de couleur en débord sur le bord du panneau,
                détail de la charte (chevron dans un rond de la teinte b). */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label={isCollapsed ? 'Déplier le menu' : 'Replier le menu'}
              className="hidden md:block p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-800 pastel:absolute pastel:-right-2.5 pastel:top-[22px] pastel:z-10 md:pastel:grid pastel:place-items-center pastel:h-6 pastel:w-6 pastel:p-0 pastel:rounded-full pastel:bg-(--p-b-sig) pastel:hover:bg-(--p-b-sig) pastel:transition-transform pastel:hover:scale-110"
            >
              <svg
                className={`w-4 h-4 pastel:w-3.5 pastel:h-3.5 text-slate-500 dark:text-slate-400 pastel:text-(--p-ink) transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>

          {/* ── History section ── */}
          <nav className="flex-grow flex flex-col p-3 gap-1 overflow-hidden">
            {/* Retour accueil explicite (lève l'ambiguïté avec la flèche de repli) */}
            <button
              onClick={onHomeClick}
              title="Nouvelle analyse"
              className={`mb-3 flex items-center rounded-lg pastel:rounded-full pastel:shadow-none pastel:transition-transform pastel:hover:scale-[1.02] bg-violet-600 text-white hover:bg-violet-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200 shader:bg-white shader:text-slate-900 shader:hover:bg-white/90 transition-colors font-medium text-sm shadow-sm ${isCollapsed ? 'justify-center p-2 pastel:p-2.5' : 'gap-2 px-3 py-2 pastel:justify-center pastel:px-4 pastel:py-2.5'}`}
            >
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {!isCollapsed && <span>Nouvelle analyse</span>}
            </button>

            {!hasHistoryAccess ? (
              !isCollapsed && (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-8 text-slate-400 dark:text-slate-500 text-sm">
                  <svg className="w-8 h-8 mb-3 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-medium text-slate-500 dark:text-slate-400 mb-1">Historique désactivé</p>
                  <p>Vos analyses ne sont pas conservées. Passez au plan Pro pour les retrouver ici.</p>
                </div>
              )
            ) : (
            <>
            {!isCollapsed && (
              <>
                {/* Search */}
                <div className="glow-input relative mb-2">
                  <svg
                    className="absolute left-2.5 pastel:left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10"
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-8 pastel:pl-10 pr-3 py-1.5 pastel:py-2 text-sm bg-slate-100 dark:bg-slate-800 pastel:bg-white/5 shader:bg-white/10 border border-slate-200 dark:border-slate-700 pastel:border-white/10 shader:border-white/10 rounded-md pastel:rounded-full outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-slate-900 dark:text-white placeholder-slate-400 pastel:placeholder-white/35"
                  />
                </div>

                {/* Category filter */}
                {usedCategories.length > 0 && (
                  <div className="glow-input mb-3">
                    <select
                      value={activeFilter}
                      onChange={e => setActiveFilter(e.target.value as AnalysisType | 'all')}
                      className="w-full text-sm px-3 pastel:px-4 py-1.5 pastel:py-2 bg-slate-100 dark:bg-slate-800 pastel:bg-white/5 shader:bg-white/10 border border-slate-200 dark:border-slate-700 pastel:border-white/10 shader:border-white/10 rounded-md pastel:rounded-full outline-none focus:ring-2 focus:ring-violet-500 text-slate-700 dark:text-slate-300 shader:text-white [&>option]:text-slate-900 dark:[&>option]:text-white dark:[&>option]:bg-slate-800"
                    >
                      <option value="all">Toutes les analyses</option>
                      {usedCategories.map(opt => (
                        <option key={opt.key} value={opt.key}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* History list */}
            <div className="flex-1 overflow-y-auto scrollbar-evin space-y-4 pb-2">
              {history.length === 0 ? (
                <div className={`text-center text-slate-500 text-sm py-8 px-2 ${isCollapsed ? 'hidden' : ''}`}>
                  Vos analyses apparaitront ici.
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className={`text-center text-slate-500 text-sm py-8 px-2 ${isCollapsed ? 'hidden' : ''}`}>
                  Aucun resultat.
                </div>
              ) : isCollapsed ? (
                // Collapsed: verdict dots only
                <div className="space-y-1">
                  {history.slice(0, 10).map(item => {
                    const opt = ANALYSIS_OPTIONS.find(o => o.key === item.analysisType);
                    return (
                      <button
                        key={item.id}
                        onClick={() => onViewHistory(item)}
                        title={item.title}
                        className={`w-full flex justify-center items-center p-2 rounded-md transition-colors ${
                          activeItemId === item.id
                            ? 'bg-slate-200 dark:bg-slate-800 shader:bg-white/15'
                            : 'hover:bg-slate-200/50 dark:hover:bg-slate-800/50 shader:hover:bg-white/10'
                        }`}
                      >
                        <AnalysisIcon type={item.analysisType} className="w-4 h-4 text-slate-400 dark:text-white/50" />
                      </button>
                    );
                  })}
                </div>
              ) : (
                // Expanded: grouped by date
                (Object.entries(grouped) as [string, SidebarItem[]][]).map(
                  ([dateLabel, items]) => (
                    <div key={dateLabel}>
                      <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 shader:text-white/50 uppercase tracking-wider px-2 mb-1">
                        {dateLabel}
                      </p>
                      <div className="space-y-0.5">
                        {items.map(item => (
                          <div key={item.id} className="group/item relative">
                            {renamingId === item.id ? (
                              <div className="px-2 py-1">
                                <input
                                  autoFocus
                                  value={renameValue}
                                  onChange={e => setRenameValue(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      onRenameHistory(item.id, renameValue.trim() || item.title);
                                      setRenamingId(null);
                                    }
                                    if (e.key === 'Escape') setRenamingId(null);
                                  }}
                                  onBlur={() => {
                                    onRenameHistory(item.id, renameValue.trim() || item.title);
                                    setRenamingId(null);
                                  }}
                                  className="w-full text-sm px-2 py-1 bg-white dark:bg-slate-700 border border-violet-500 rounded-md outline-none text-slate-900 dark:text-white"
                                />
                              </div>
                            ) : (
                              <button
                                onClick={() => onViewHistory(item)}
                                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors pr-16 ${
                                  activeItemId === item.id
                                    ? 'bg-slate-200 dark:bg-slate-800 shader:bg-white/15 text-slate-900 dark:text-white'
                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 shader:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {item.status === 'running' || item.status === 'queued' ? (
                                    <svg className="w-2.5 h-2.5 flex-shrink-0 animate-spin text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                                      <path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" />
                                    </svg>
                                  ) : item.status === 'error' ? (
                                    <span className="w-2 h-2 rounded-full flex-shrink-0 bg-red-500" />
                                  ) : !item.read ? (
                                    // Pastille bleue = analyse terminée non encore consultée.
                                    <span className="w-2 h-2 rounded-full flex-shrink-0 bg-violet-500 pastel:bg-(--p-b-sig)" title="Non lu" />
                                  ) : (
                                    // Lu : pas de pastille (spacer pour garder l'alignement).
                                    <span className="w-2 h-2 flex-shrink-0" />
                                  )}
                                  <p className="truncate font-medium text-slate-800 dark:text-slate-200 shader:text-white">
                                    {item.title}
                                  </p>
                                </div>
                                <p className="text-xs text-slate-400 dark:text-slate-500 shader:text-white/70 truncate mt-0.5 ml-4">
                                  {item.analysisLabel}
                                  {item.status === 'running' && <>{' '}&middot;{' '}<span className="text-violet-500">analyse en cours…</span></>}
                                  {item.status === 'queued' && <>{' '}&middot;{' '}<span className="text-violet-500">en attente…</span></>}
                                  {item.status === 'error' && <>{' '}&middot;{' '}<span className="text-red-500">échec</span></>}
                                  {item.status === 'done' && item.score >= 0 && (
                                    <>
                                      {' '}&middot;{' '}
                                      <span className={verdictColorFromScore(item.score)}>{item.score}/100</span>
                                    </>
                                  )}
                                </p>
                              </button>
                            )}

                            {/* Hover actions — masquées tant que l'analyse tourne */}
                            {renamingId !== item.id && item.status !== 'running' && item.status !== 'queued' && (
                              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover/item:opacity-100 transition-opacity">
                                {/* Rename */}
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    setRenamingId(item.id);
                                    setRenameValue(item.title);
                                  }}
                                  title="Renommer"
                                  className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                </button>
                                {/* Delete */}
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (window.confirm("Supprimer cette analyse de l'historique ?")) {
                                      onDeleteHistory(item.id);
                                    }
                                  }}
                                  title="Supprimer"
                                  className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                    <path d="M10 11v6" /><path d="M14 11v6" />
                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                )
              )}
            </div>
            </>
            )}
          </nav>

          {/* ── Footer: user account ── (hauteur calée sur le Footer principal pour aligner les bordures hautes) */}
          <div className="border-t border-slate-200 dark:border-slate-800 shader:border-white/10 pastel:border-white/8 flex items-center px-3 h-[61px] shrink-0">
            <button
              onClick={onAccountClick}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 shader:hover:bg-white/10 ${isCollapsed ? 'justify-center' : ''}`}
            >
              <Avatar
                avatarUrl={user?.avatarUrl}
                name={user?.displayName}
                email={user?.email}
                className="w-7 h-7"
                textClassName="text-[10px]"
              />
              {!isCollapsed && (
                <div className="flex flex-col items-start min-w-0">
                  <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-200 max-w-full">
                    {user?.displayName || user?.email || 'Mon Compte'}
                  </span>
                  <span className="text-xs text-slate-400 dark:text-slate-500 shader:text-white">
                    {({ free: 'Gratuit', beta: 'Accès offert', pro: 'Pro', premium: 'Premium', enterprise: 'Enterprise' } as Record<string, string>)[organization?.plan ?? 'free'] ?? 'Gratuit'}
                  </span>
                </div>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
